import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./ReactionGame.css";

const ReactionGame = () => {

  const navigate = useNavigate();

  const initialForm = () => ({
    name: "",
    email: localStorage.getItem("rg_email") || "",
    sex: "",
    phone: localStorage.getItem("rg_phone") || "",
    age: "",
  });

  const [formData] = useState(initialForm);
  const [reactionTime, setReactionTime] = useState(0);
  const [attemptWrongClicks, setAttemptWrongClicks] = useState(0);
  const [clickCount, setClickCount] = useState(0);
  const [wrongClickCount, setWrongClickCount] = useState(0);
  const [results, setResults] = useState([]);
  const [countdown, setCountdown] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [page, setPage] = useState("game");

  const startTimeRef = useRef(0);
  const greenReadyRef = useRef(false);
  const clickedRef = useRef(false);
  const canClickRef = useRef(false);
  const timeoutRef = useRef(null);
  const circleRef = useRef(null);

  /* ================= START GAME ================= */
  const startGame = useCallback(() => {
    window.scrollTo(0, 0);

    clickedRef.current = false;
    greenReadyRef.current = false;
    canClickRef.current = false;
    startTimeRef.current = 0;

    setCountdown(null);

    if (circleRef.current) {
      circleRef.current.classList.remove("green");
      circleRef.current.classList.add("red");
    }

    const randomDelay = Math.random() * 3000 + 2000;

    timeoutRef.current = setTimeout(() => {
      if (circleRef.current) {
        circleRef.current.classList.remove("red");
        circleRef.current.classList.add("green");
      }

      // ✅ FIX: Set timer IMMEDIATELY — no rAF wrapper
      // Python sets startTimer = time.time() right when color changes
      // rAF was adding ~16ms delay before timer even started
      startTimeRef.current = performance.now();
      greenReadyRef.current = true;
      canClickRef.current = true;

    }, randomDelay);

  }, []);

  /* ================= INITIAL START ================= */
  useEffect(() => {
    startGame();
    return () => clearTimeout(timeoutRef.current);
  }, [startGame]);

  /* ================= CLICK HANDLER ================= */
  const handleCircleClick = useCallback(() => {

    if (!canClickRef.current || !greenReadyRef.current) {
      setWrongClickCount(prev => prev + 1);
      setAttemptWrongClicks(prev => prev + 1);
      return;
    }

    if (clickedRef.current) return;
    clickedRef.current = true;

    // ✅ FIX: Capture end time FIRST before anything else
    const endTime = performance.now();

    clearTimeout(timeoutRef.current);
    canClickRef.current = false;
    greenReadyRef.current = false;

    if (startTimeRef.current === 0) return;

    const rawTime = (endTime - startTimeRef.current) / 1000;

    


    const timeTaken = rawTime.toFixed(3);

    setReactionTime(timeTaken);
    setClickCount(prev => prev + 1);
    setResults(prev => [
      ...prev,
      { time: timeTaken, wrongClicks: attemptWrongClicks }
    ]);

    setAttemptWrongClicks(0);
    setCountdown(5);

  }, [attemptWrongClicks]);

  /* ================= SAVE DATA ================= */
  const saveGameData = useCallback(async () => {
    try {
      await axios.post("/save", {
        ...formData,
        results,
        wrongClickCount
      });
    } catch (error) {
      console.error("Save error:", error);
    }
  }, [formData, results, wrongClickCount]);

  /* ================= COUNTDOWN ================= */
  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      if (circleRef.current) {
        circleRef.current.classList.remove("green");
        circleRef.current.classList.add("red");
      }
      greenReadyRef.current = false;
      canClickRef.current = false;

      if (clickCount < 5) {
        startGame();
      } else {
        setPage("result");
        saveGameData();
      }
    }
  }, [countdown, clickCount, saveGameData, startGame]);

  /* ================= VALID ATTEMPTS ================= */
  const validAttempts = results
    .map(item => Number(item.time))
    .filter(time => time >= 0.150 && time <= 1);

  const currentAverage =
    validAttempts.length > 0
      ? validAttempts.reduce((a, b) => a + b, 0) / validAttempts.length
      : null;

  /* ================= FETCH LEADERBOARD ================= */
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const email = localStorage.getItem("rg_email");
        const res = await axios.get(`/api/leaderboard?email=${email}`);
        setLeaderboard(res.data.leaderboard || []);
        setUserRank(res.data.userRank);
      } catch (error) {
        console.error("Leaderboard fetch error:", error);
      }
    };

    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 2000);
    return () => clearInterval(interval);
  }, []);

  /* ================= TRY AGAIN ================= */
  const handleTryAgain = () => {
    setPage("game");
    setClickCount(0);
    setWrongClickCount(0);
    setReactionTime(0);
    setResults([]);
    setAttemptWrongClicks(0);

    greenReadyRef.current = false;
    canClickRef.current = false;
    clickedRef.current = false;
    startTimeRef.current = 0;

    if (circleRef.current) {
      circleRef.current.classList.remove("green");
      circleRef.current.classList.add("red");
    }

    startGame();
  };

  /* ================= EXIT ================= */
  const handleExit = async () => {
    await saveGameData();
    navigate("/reactiongame");
  };

  return (
    <div className="containerr">
      {page === "game" && (
        <div className="game-containerr">
          <div
            ref={circleRef}
            className="circler red"
            onPointerDown={(e) => {
              e.preventDefault();
              handleCircleClick();
            }}
          ></div>

          <div className="game-stats">
            <p className="attempt-counter">
            Attempt: {Math.min(results.length, 5)}/5
          </p>
            <p className="reaction-timer">
              Reaction Time: {(reactionTime * 1000).toFixed(0)} ms
            </p>
            <p className="countdownr">
              {countdown !== null ? `Countdown: ${countdown}` : "\u00A0"}
            </p>
            <p className="wrong-clickr">
              Wrong Clicks: {wrongClickCount}
            </p>
          </div>
        </div>
      )}

      {page === "result" && (
        <div className="result-big-container">
          <div className="leaderboard-box">
            <h3>Leaderboard</h3>
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Name</th>
                  <th>Best Reaction</th>
                  <th>Medal</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.slice(0, 5).map((player, index) => {
                  let medal = "";
                  if (index === 0) medal = "🥇";
                  else if (index === 1) medal = "🥈";
                  else if (index === 2) medal = "🥉";
                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{player.name}</td>
                      <td>{(player.bestPoints * 1000).toFixed(0)} ms</td>
                      <td>{medal}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {userRank && (
              <div className="your-rank-box">
                <p><b>Your Rank:</b> {userRank.rank}</p>
                <p><b>Avg Reaction:</b> {currentAverage ? (currentAverage * 1000).toFixed(0) + " ms" : "N/A"}</p>
                <p><b>Percentile:</b> {userRank.percentile}%</p>
              </div>
            )}
          </div>

          <div className="results-box">
            <h2>Your Attempts</h2>
            <ul>
              {results.map((item, index) => (
                <li key={index}>
                  Attempt {index + 1}: {(Number(item.time) * 1000).toFixed(0)} ms
                  {" | "}
                  Wrong: {item.wrongClicks}
                </li>
              ))}
            </ul>
            <button onClick={handleTryAgain} className="try-again-buttonr">Try Again</button>
            <button onClick={handleExit} className="exit-buttonr">Exit</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReactionGame;
